import React, { useState, useEffect } from 'react';
import styles from './FuturisticAdmin.module.css';
import { userPresenceService } from '../../services/userPresenceService';

interface UserVisit {
  id: string;
  email?: string;
  url: string;
  pageTitle?: string;
  visitedAt: string;
  duration: number;
  sessionId: string;
  deviceType?: string;
  browser?: string;
  country?: string;
  city?: string;
  isAuthenticated: boolean;
  user?: {
    email: string;
  };
}

interface PresenceData {
  data: UserVisit[];
  total: number;
  page: number;
  totalPages: number;
}

interface PresenceAnalytics {
  totalVisits: number;
  uniqueVisitors: number;
  avgSessionDuration: number;
  topPages: Array<{
    url: string;
    pageTitle: string;
    visits: number;
    percentage: number;
  }>;
}

const UserPresenceManager: React.FC = () => {
  const [presenceData, setPresenceData] = useState<PresenceData | null>(null);
  const [analytics, setAnalytics] = useState<PresenceAnalytics | null>(null);
  const [activeUsers, setActiveUsers] = useState<UserVisit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'month' | 'all'>('all');
  const [recordLimit, setRecordLimit] = useState<number>(1000);
  const [groupByUser, setGroupByUser] = useState<boolean>(false);

  useEffect(() => {
    loadAllData();
  }, [timeFilter, recordLimit]);

  const getDateRange = (filter: 'today' | 'week' | 'month' | 'all') => {
    const now = new Date();
    let startDate: Date | null = null;

    switch (filter) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - 7);
        startDate = weekStart;
        break;
      case 'month':
        const monthStart = new Date(now);
        monthStart.setDate(now.getDate() - 30);
        startDate = monthStart;
        break;
      case 'all':
      default:
        startDate = null;
        break;
    }

    return startDate;
  };

  const filterDataByTime = (data: UserVisit[]) => {
    const startDate = getDateRange(timeFilter);
    if (!startDate) return data;

    return data.filter(visit => new Date(visit.visitedAt) >= startDate);
  };

  const groupVisitsByUser = (visits: UserVisit[]): UserVisit[] => {
    if (!groupByUser) return visits;

    const userMap = new Map<string, UserVisit>();
    
    visits.forEach(visit => {
      // Group all anonymous users together under 'anonymous'
      const userKey = visit.user?.email || visit.email || 'anonymous';
      
      // Keep the most recent visit for each user
      if (!userMap.has(userKey) || new Date(visit.visitedAt) > new Date(userMap.get(userKey)!.visitedAt)) {
        userMap.set(userKey, visit);
      }
    });
    
    return Array.from(userMap.values()).sort((a, b) => 
      new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime()
    );
  };

  const calculateAnalytics = (visits: UserVisit[]) => {
    const filteredVisits = filterDataByTime(visits);
    
    // Count total visits (each page visit counts)
    const totalVisits = filteredVisits.length;
    
    // Count unique visitors by email/user ID
    const uniqueEmails = new Set(
      filteredVisits
        .map(visit => visit.user?.email || visit.email)
        .filter(email => email) // Remove null/undefined
    );
    const uniqueVisitors = uniqueEmails.size;
    
    // Calculate average session duration
    const sessionDurations = new Map<string, number>();
    filteredVisits.forEach(visit => {
      if (visit.duration > 0) {
        const currentDuration = sessionDurations.get(visit.sessionId) || 0;
        sessionDurations.set(visit.sessionId, Math.max(currentDuration, visit.duration));
      }
    });
    
    const avgDuration = sessionDurations.size > 0 
      ? Array.from(sessionDurations.values()).reduce((a, b) => a + b, 0) / sessionDurations.size
      : 0;

    // Top pages analysis
    const pageVisits = new Map<string, { count: number; title: string }>();
    filteredVisits.forEach(visit => {
      const url = visit.url;
      const current = pageVisits.get(url) || { count: 0, title: visit.pageTitle || 'Untitled' };
      pageVisits.set(url, { count: current.count + 1, title: current.title });
    });

    const topPages = Array.from(pageVisits.entries())
      .map(([url, data]) => ({
        url,
        pageTitle: data.title,
        visits: data.count,
        percentage: totalVisits > 0 ? (data.count / totalVisits) * 100 : 0
      }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 5);

    return {
      totalVisits,
      uniqueVisitors,
      avgSessionDuration: Math.round(avgDuration),
      topPages
    };
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load presence data and active users
      const [presenceResponse, activeResponse] = await Promise.all([
        userPresenceService.getUserPresence({ page: 1, limit: recordLimit }),
        userPresenceService.getActiveUsers(30)
      ]);

      setPresenceData(presenceResponse);
      setActiveUsers(activeResponse);
      
      // Calculate analytics from the actual data
      const calculatedAnalytics = calculateAnalytics(presenceResponse.data || []);
      setAnalytics(calculatedAnalytics);
      
      console.log('✅ Loaded presence data:', presenceResponse);
      console.log('✅ Calculated analytics:', calculatedAnalytics);
      console.log('✅ Loaded active users:', activeResponse);
      
    } catch (error) {
      console.error('❌ Failed to load presence data:', error);
      setError('Failed to load presence data. Make sure you\'re logged in as admin.');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search;
    } catch {
      return url;
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ color: 'white', padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔄</div>
        <h3>Loading user presence data...</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'white', padding: '2rem' }}>
        <div style={{ 
          backgroundColor: 'rgba(239, 68, 68, 0.1)', 
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>❌</div>
          <h3 style={{ color: '#ef4444', margin: '0 0 1rem 0' }}>Error Loading Data</h3>
          <p style={{ color: '#e2e8f0', margin: '0 0 1rem 0' }}>{error}</p>
          <button 
            onClick={loadAllData}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ color: 'white', padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 className={styles.testTitle}>👀 User Presence Tracking</h1>
        <p style={{ color: '#06b6d4', fontSize: '1.1rem', marginBottom: '1rem' }}>
          Live tracking of user visits and activity
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ 
            backgroundColor: 'rgba(16, 185, 129, 0.1)', 
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '0.9rem'
          }}>
            <span style={{ color: '#10b981' }}>✅ Live Data Connected</span>
          </div>
          
          {/* Time Filter Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>Time Period:</span>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as 'today' | 'week' | 'month' | 'all')}
              style={{
                backgroundColor: 'rgba(51, 65, 85, 0.5)',
                border: '1px solid rgba(51, 65, 85, 0.5)',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                color: 'white',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="all">All Time</option>
            </select>
          </div>
          
          {/* Record Limit Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>Records:</span>
            <select
              value={recordLimit}
              onChange={(e) => setRecordLimit(Number(e.target.value))}
              style={{
                backgroundColor: 'rgba(51, 65, 85, 0.5)',
                border: '1px solid rgba(51, 65, 85, 0.5)',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                color: 'white',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value={1000}>1,000</option>
              <option value={2000}>2,000</option>
              <option value={5000}>5,000</option>
              <option value={10000}>10,000</option>
            </select>
          </div>
          
          <button 
            onClick={loadAllData}
            style={{
              backgroundColor: 'rgba(6, 182, 212, 0.2)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              color: '#06b6d4',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '2rem' 
      }}>
        <div className={styles.card}>
          <h3 style={{ color: '#06b6d4', fontSize: '1rem', marginBottom: '0.5rem' }}>Total Visits</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
            {analytics?.totalVisits || 0}
          </p>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
            {timeFilter === 'today' ? 'Today' : 
             timeFilter === 'week' ? 'This week' : 
             timeFilter === 'month' ? 'This month' : 'All time'}
          </p>
        </div>
        
        <div className={styles.card}>
          <h3 style={{ color: '#8b5cf6', fontSize: '1rem', marginBottom: '0.5rem' }}>Unique Visitors</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
            {analytics?.uniqueVisitors || 0}
          </p>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
            {timeFilter === 'today' ? 'Today' : 
             timeFilter === 'week' ? 'This week' : 
             timeFilter === 'month' ? 'This month' : 'All time'}
          </p>
        </div>
        
        <div className={styles.card}>
          <h3 style={{ color: '#10b981', fontSize: '1rem', marginBottom: '0.5rem' }}>Avg Session</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
            {analytics?.avgSessionDuration ? formatDuration(analytics.avgSessionDuration) : '0s'}
          </p>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
            {timeFilter === 'today' ? 'Today' : 
             timeFilter === 'week' ? 'This week' : 
             timeFilter === 'month' ? 'This month' : 'All time'}
          </p>
        </div>
        
        <div className={styles.card}>
          <h3 style={{ color: '#f59e0b', fontSize: '1rem', marginBottom: '0.5rem' }}>Active Now</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
            {activeUsers.length}
          </p>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>Last 30 min</p>
        </div>
      </div>

      {/* User Visits Table */}
      <div className={styles.card} style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.5)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
              Recent Visits ({groupVisitsByUser(filterDataByTime(presenceData?.data || [])).length} 
              {timeFilter === 'today' ? ' today' : 
               timeFilter === 'week' ? ' this week' : 
               timeFilter === 'month' ? ' this month' : ' total'})
            </h3>
            
            {/* Group by User Checkbox */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              cursor: 'pointer',
              color: '#e2e8f0',
              fontSize: '0.9rem'
            }}>
              <input
                type="checkbox"
                checked={groupByUser}
                onChange={(e) => setGroupByUser(e.target.checked)}
                style={{ 
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px'
                }}
              />
              <span>Group by User</span>
            </label>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
            {groupByUser 
              ? 'Showing most recent visit per user' 
              : 'Real-time user activity tracking • Each page visit counts separately'}
          </p>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>User</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>URL Visited</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Page Title</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Duration</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Device</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Location</th>
              </tr>
            </thead>
            <tbody>
              {presenceData && presenceData.data.length > 0 ? (
                groupVisitsByUser(filterDataByTime(presenceData.data)).map((visit, index) => (
                  <tr 
                    key={visit.id} 
                    style={{ 
                      borderTop: index > 0 ? '1px solid rgba(51, 65, 85, 0.5)' : 'none',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(51, 65, 85, 0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div 
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            borderRadius: '50%', 
                            backgroundColor: visit.isAuthenticated ? '#10b981' : '#6b7280' 
                          }}
                        ></div>
                        <span style={{ color: 'white' }}>
                          {visit.user?.email || visit.email || 'Anonymous'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', maxWidth: '300px' }}>
                      <code style={{ 
                        color: '#06b6d4', 
                        fontSize: '0.875rem', 
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        display: 'block',
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word'
                      }}>
                        {formatUrl(visit.url)}
                      </code>
                    </td>
                    <td style={{ padding: '1rem', color: '#e2e8f0' }}>
                      {visit.pageTitle || 'Untitled'}
                    </td>
                    <td style={{ padding: '1rem', color: '#e2e8f0', fontSize: '0.875rem' }}>
                      {formatTimestamp(visit.visitedAt)}
                    </td>
                    <td style={{ padding: '1rem', color: '#e2e8f0' }}>
                      {visit.duration > 0 ? formatDuration(visit.duration) : '-'}
                    </td>
                    <td style={{ padding: '1rem', color: '#e2e8f0', fontSize: '0.875rem' }}>
                      {visit.deviceType} • {visit.browser}
                    </td>
                    <td style={{ padding: '1rem', color: '#e2e8f0', fontSize: '0.875rem' }}>
                      {visit.city && visit.country ? `${visit.city}, ${visit.country}` : visit.country || '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                    No visits recorded for the selected time period. 
                    {timeFilter !== 'all' && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <button 
                          onClick={() => setTimeFilter('all')} 
                          style={{
                            background: 'none',
                            border: '1px solid rgba(6, 182, 212, 0.3)',
                            color: '#06b6d4',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          View all time
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserPresenceManager;
